import { post } from './apiClient';

export function requestPresignedUploadUrl({ path, fileName, contentType, category, projectId, fileSize }) {
  return post('/api/uploads/presigned-url', { path, fileName, contentType, category, projectId, fileSize });
}

export function completeUpload({ assetId, fileName, s3Key, category, projectId, fileSize, contentType }) {
  return post('/api/uploads/complete', { assetId, fileName, s3Key, category, projectId, fileSize, contentType });
}

export function completeMultipartUpload({ s3Key, uploadId, parts }) {
  return post('/api/uploads/multipart/complete', { s3Key, uploadId, parts });
}

export function abortMultipartUpload({ s3Key, uploadId }) {
  return post('/api/uploads/multipart/abort', { s3Key, uploadId });
}

/**
 * Uploads a file directly to S3 using a pre-signed PUT URL.
 * For files under ~4.5 GB.
 */
export async function uploadFileToS3(presignedUrl, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presignedUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

/**
 * Uploads a large file via S3 multipart upload using presigned part URLs.
 * Each part is uploaded sequentially with progress tracking.
 */
export async function uploadFileMultipart(partUrls, chunkSize, file, onProgress) {
  const parts = [];
  const totalSize = file.size;
  let uploaded = 0;

  for (const { partNumber, url } of partUrls) {
    const start = (partNumber - 1) * chunkSize;
    const end = Math.min(start + chunkSize, totalSize);
    const blob = file.slice(start, end);

    const etag = await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          const current = uploaded + e.loaded;
          onProgress(Math.round((current / totalSize) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const responseEtag = xhr.getResponseHeader('ETag');
          resolve(responseEtag);
        } else {
          reject(new Error(`Part ${partNumber} upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error(`Part ${partNumber} upload failed`));
      xhr.send(blob);
    });

    uploaded = end;
    parts.push({ partNumber, etag });
  }

  return parts;
}
